import { SharedServiceDataItem, SharingType } from '../../mru/shared-with-me/shared-with-me.model';
import { AttachmentType } from '../models/email-attachment.model';
import { FileItem, FileType } from '../models/file-item.model';
import { GraphSharepointIds } from '../models/onedrive-graph.model';
import { GraphOneNoteNotebook } from '../models/onenote-graph.model';
import { ClassNoteBook } from '../models/onenote.model';
import { AuthType } from '../shared-context/client-startup-config.service';
import { LocService } from '../shared-context/loc.service';
import { UrlEncoder } from '../utilities/url-encoder.utility';
import { Form, FormsHelperFunctions, FormType } from './../models/form.model';
import { MruDataItem } from './mru-data-item.model';
import { DocumentThumbnails, MruFileFormat, MruFileItem } from './mru-file-item.model';

function getPathFromUrl(url: string): Array<string> {
    if (url) {
        // If url is malformed decoding can throw error
        const decodedUrl = UrlEncoder.decodeUrl(url);

        if (decodedUrl) {
            const path = decodedUrl.split('://');
            if (path.length > 1) {
                const pathArray = path[1].split('/');
                pathArray.pop();
                return pathArray;
            }
        }
    }

    return [];
}

function getMRUFileFormat(category: string): MruFileFormat {
    let fileFormat: MruFileFormat = MruFileFormat.OfficeFeedDocument;
    switch (category.toLocaleLowerCase()) {
        case 'upcomingmeeting':
            fileFormat = MruFileFormat.UpcomingMeeting;
            break;
        case 'newsarticle':
            fileFormat = MruFileFormat.News;
            break;
        case 'streamsvideo':
            fileFormat = MruFileFormat.StreamsVideo;
            break;
        case 'myanalyticsinsight':
            fileFormat = MruFileFormat.MyAnalytics;
            break;
    }
    return fileFormat;
}

/**
 * Static utility class for converting between MruDataItems and MruFileItems
 */
export function toFileItemFromRecent(
    mruData: MruDataItem,
    elementId: string,
    impressionId: string,
    locService: LocService,
    isRtl: boolean,
    authType: AuthType,
    fromCache: boolean
): MruFileItem {
    let sharepointIds: GraphSharepointIds;
    let createdDate: Date;
    let driveId: string;
    let thumbnailId: string;
    let attributionOverride: string;
    const metaData = mruData.MetaData;
    const fileType = FileItem.fileTypeNameToFileType(mruData.FileType);
    if (metaData) {
        if (metaData.SPListId && metaData.SPListItemUniqueId && metaData.SPSiteId && metaData.SPSiteUrl && metaData.SPWebId) {
            sharepointIds = {
                listId: metaData.SPListId,
                listItemId: null,
                listItemUniqueId: metaData.SPListItemUniqueId,
                siteId: metaData.SPSiteId,
                siteUrl: metaData.SPSiteUrl,
                webId: metaData.SPWebId
            };
        }
        if (metaData.CreatedDate) {
            createdDate = new Date(metaData.CreatedDate);
        }
        if (metaData.DriveId) {
            driveId = metaData.DriveId;
        }

        const swayMetaData = metaData.SwayMetaData;
        if (swayMetaData) {
            thumbnailId = swayMetaData.ThumbnailId;
            attributionOverride = swayMetaData.IsTransformedFromWord ? locService.SwayTransformedFromWord : null;
        }
    }

    const formsMetaData = (mruData && mruData.MetaData && fileType === FileType.Forms) ? mruData.MetaData.FormsMetaData : null;

    const mruFileItem = new MruFileItem(
        MruFileFormat.Recent,
        elementId,
        impressionId,
        mruData.DocId || mruData.ServerDocId,
        driveId || mruData.DriveId,
        mruData.FileName,
        mruData.Title,
        mruData.Url,
        mruData.WebUrl,
        null,
        null,
        mruData.LastAccessedTime,
        null,
        fileType,
        SharingType.None,
        FileItem.NameToProvenance[mruData.Provenance],
        mruData.PathArray,
        isRtl,
        authType,
        fromCache,
        mruData.Application,
        mruData.ResourceId,
        mruData.StorageHost,
        mruData.UrlAlreadyEncoded,
        mruData.IsPinned,
        null,
        fileType === FileType.PowerBI ? mruData.MetaData.PowerBIMetaData.breadcrumb_url : null,
        mruData.Category,
        mruData.MruV2Type,
        mruData.MruId,
        mruData.Activity,
        false,
        mruData.IsLocal,
        createdDate,
        sharepointIds,
        typeof mruData.Sharing !== 'undefined' ? { shared: !!mruData.Sharing } : undefined,
        mruData.LifecycleInfo,
        null,
        null,
        null,
        null,
        {
            thumbnailId
        },
        attributionOverride,
        null,
        false,
        mruData.ServiceId,
        null,
        null,
        mruData.FeedbackId,
        mruData.ServerDocId,
        formsMetaData ? formsMetaData.owner_name : mruData && mruData.MetaData ? mruData.MetaData.CreatedByUserName : null,
        null,
        mruData.Extension ? FormsHelperFunctions.getExtensionToFormType(mruData.Extension) : (formsMetaData && formsMetaData.owner_type ? FormsHelperFunctions.getOwnerPrincipalTypeToFormType(formsMetaData.owner_type) : null),
        formsMetaData ? formsMetaData.owner_id : null,
        formsMetaData ? formsMetaData.owner_tenant_id : null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        formsMetaData ? formsMetaData.form_type : null,
        formsMetaData ? formsMetaData.category : null
    );
    mruFileItem.metaData = formsMetaData;
    mruFileItem.extension = mruData.Extension;

    return mruFileItem;
}

export function toFileItemFromRecommended(
    mruData: MruDataItem,
    elementId: string,
    impressionId: string,
    isRtl: boolean,
    authType: AuthType,
    fromCache: boolean
): MruFileItem {
    let sharepointIds: GraphSharepointIds;
    const rawSharepointIds = mruData.SharepointInfo;
    if (rawSharepointIds && rawSharepointIds.list_id && rawSharepointIds.list_item_unique_id && rawSharepointIds.site_id && rawSharepointIds.site_url && rawSharepointIds.web_id) {
        sharepointIds = {
            listId: rawSharepointIds.list_id,
            listItemId: null,
            listItemUniqueId: rawSharepointIds.list_item_unique_id,
            siteId: rawSharepointIds.site_id,
            siteUrl: rawSharepointIds.site_url,
            webId: rawSharepointIds.web_id
        };
    }
    const mruFileItem = new MruFileItem(
        MruFileFormat.Recommended,
        elementId,
        impressionId,
        mruData.DocId,
        mruData.DriveId,
        mruData.FileName,
        mruData.Title,
        mruData.Url,
        mruData.WebUrl,
        null,
        mruData.LastAccessedTime,
        mruData.LastAccessedTime,
        null,
        FileItem.fileTypeNameToFileType(mruData.FileType),
        SharingType.None,
        FileItem.NameToProvenance[mruData.Provenance],
        mruData.IsEmailAttachment ? [] : getPathFromUrl(mruData.Url),
        isRtl,
        authType,
        fromCache,
        mruData.Application,
        mruData.ResourceId,
        mruData.StorageHost,
        mruData.UrlAlreadyEncoded,
        mruData.IsPinned,
        null,
        null,
        mruData.Category,
        null,
        null,
        mruData.Activity,
        mruData.IsEmailAttachment,
        mruData.IsLocal,
        null,
        sharepointIds,
        null,
        false,
        mruData.EmailReference,
        null,
        null,
        null,
        null,
        null,
        mruData.SPOId,
        mruData.IsCollab,
        null,
        mruData.NavigationId,
        null,
        null,
        mruData.ServerDocId,
        null,
        mruData.TeamsDeepLink
    );
    mruFileItem.iconUrl = mruData.IconUrl;
    mruFileItem.extension = mruData.Extension;
    return mruFileItem;
}

export function toFileItemFromOfficeFeed(
    mruData: MruDataItem,
    elementId: string,
    impressionId: string,
    isRtl: boolean,
    authType: AuthType,
    fromCache: boolean,
    trending: boolean = false
): MruFileItem {
    let sharepointIds: GraphSharepointIds;
    const rawSharepointIds = mruData.SharepointInfo;
    if (rawSharepointIds) {
        sharepointIds = {
            listId: rawSharepointIds.list_id,
            listItemId: null,
            listItemUniqueId: rawSharepointIds.list_item_unique_id,
            siteId: rawSharepointIds.site_id,
            siteUrl: rawSharepointIds.site_url,
            webId: rawSharepointIds.web_id
        };
    }
    let thumbnails: DocumentThumbnails;
    if (mruData.ThumbnailUrl) {
        thumbnails = {
            small: mruData.ThumbnailUrl,
            medium: mruData.ThumbnailUrl,
            large: mruData.ThumbnailUrl,
            recommendedThumbnail: mruData.ThumbnailUrl
        };
    }

    const mruFileItem = new MruFileItem(
        trending ? MruFileFormat.Trending : getMRUFileFormat(mruData.Category),
        elementId,
        impressionId,
        mruData.DocId,
        mruData.DriveId,
        mruData.FileName,
        mruData.Title,
        mruData.Url,
        mruData.WebUrl,
        null,
        mruData.LastAccessedTime,
        mruData.LastAccessedTime,
        null,
        FileItem.fileTypeNameToFileType(mruData.FileType),
        SharingType.None,
        FileItem.NameToProvenance[mruData.Provenance],
        mruData.PathArray || (mruData.IsEmailAttachment ? [] : getPathFromUrl(mruData.Url)),
        isRtl,
        authType,
        fromCache,
        mruData.Application,
        mruData.ResourceId,
        mruData.StorageHost,
        mruData.UrlAlreadyEncoded,
        mruData.IsPinned,
        null,
        null,
        mruData.Category,
        null,
        null,
        mruData.Activity,
        mruData.IsEmailAttachment,
        false,
        null,
        sharepointIds,
        null,
        false,
        mruData.EmailReference,
        null,
        null,
        null,
        null,
        null,
        mruData.SPOId,
        mruData.IsCollab,
        null,
        mruData.NavigationId,
        thumbnails,
        mruData.FeedbackId,
        mruData.ServerDocId,
        null,
        mruData.TeamsDeepLink,
        null,
        null,
        null,
        false,
        false,
        null,
        null,
        null,
        null,
        null,
        null,
        [],
        null,
        null,
        null,
        mruData.MediaDuration
    );

    if (mruData.Category.toLowerCase() === 'upcomingmeeting') {
        mruFileItem.startTime = mruData.MeetingStart;
        mruFileItem.endTime = mruData.MeetingEnd;
        mruFileItem.location = mruData.Location;
        mruFileItem.attendees = mruData.Attendees;
        mruFileItem.joinUrl = mruData.JoinUrl;
        mruFileItem.organizer = mruData.Organizer;
        mruFileItem.joinMeetingText = mruData.JoinMeetingText;
        mruFileItem.isUpcomingMeetingAccepted = mruData.IsUpcomingMeetingAccepted;
        mruFileItem.isTeamsMeeting = mruData.IsTeamsMeeting;
    } else if (mruData.Category.toLocaleLowerCase() === 'myanalyticsinsight') {
        mruFileItem.taskInsight = mruData.TaskInsight;
        mruFileItem.potentialAction = mruData.PotentialAction;
        mruFileItem.insightName = mruData.InsightName;
        mruFileItem.insightDescription = mruData.InsightDescription;
        mruFileItem.insightDescriptionPrefix = mruData.InsightDescriptionPrefix;
        mruFileItem.activityUser = mruData.Actor;
        mruFileItem.url = mruData.Url;
    }

    mruFileItem.iconUrl = mruData.IconUrl;
    mruFileItem.extension = mruData.Extension;
    return mruFileItem;
}

export function toFileItemFromNoteBook(
    notebook: GraphOneNoteNotebook,
    elementId: string,
    impressionId: string,
    isRtl: boolean,
    authType: AuthType,
    fromCache: boolean
): MruFileItem {
    return new MruFileItem(
        MruFileFormat.NoteBook,
        elementId,
        impressionId,
        notebook.id,
        null,
        notebook.displayName,
        notebook.displayName,
        notebook.links.oneNoteWebUrl.href,
        null,
        null,
        new Date(notebook.lastModifiedDateTime),
        null,
        null,
        FileType.OneNote,
        null,
        FileItem.NameToProvenance[notebook.displayName], //TODO: provenance not given?
        getPathFromUrl(notebook.links.oneNoteWebUrl.href),
        isRtl,
        authType,
        fromCache,
        FileItem.fileTypeNameFromType(FileType.OneNote),
        null,
        null,
        true,
        false,
        {
            fullName: notebook.displayName,
            email: null
        },
        null,
        null,
        null,
        null,
        null,
        false,
        false,
        new Date(notebook.createdDateTime)
    );
}

export function toFileItemFromForm(
    form: Form,
    elementId: string,
    impressionId: string,
    isRtl: boolean,
    authType: AuthType,
    fromCache: boolean,
    mruFileFormat?: MruFileFormat,
    formType?: FormType
): MruFileItem {
    return new MruFileItem(
        mruFileFormat ? mruFileFormat : MruFileFormat.Form,
        elementId,
        impressionId,
        form.id,
        null,
        form.title,
        form.title,
        form.url,
        null,
        null,
        new Date(form.modifiedDate),
        null,
        new Date(form.dateTimeShared),
        FileType.Forms,
        // Todo: Need to find where Forms is Stored
        SharingType.None,
        null,
        // Todo: M, Forms does not return a url for the form, need to make another call. below
        null,
        isRtl,
        authType,
        fromCache,
        FileType[FileType.Forms],
        null,
        null,
        true,
        false,
        // Todo: add modified by user if available
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        {
            thumbnailId: form.otherInfo && form.otherInfo.Theme && form.otherInfo.Theme.Name ? form.otherInfo.Theme.Name : null,
            thumbnailUrl: form.background ? form.background.resourceUrl : null,
            thumbnailFillColor: form.otherInfo ? form.otherInfo.SecondaryCustomizedThemeColor : null
        },
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        form.id,
        form.ownerDisplayName,
        null,
        formType ? formType : form.ownerPrincipalType,
        form.ownerId,
        form.ownerTenantId,
        !!form.softDeleted,
        false,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        form.type,
        form.category
    );
}

export function toFileItemFromClassNoteBook(
    classnotebook: ClassNoteBook,
    elementId: string,
    impressionId: string,
    isRtl: boolean,
    authType,
    fromCache: boolean
): MruFileItem {
    return new MruFileItem(
        MruFileFormat.ClassNoteBook,
        elementId,
        impressionId,
        classnotebook.id,
        null,
        classnotebook.name,
        classnotebook.name,
        classnotebook.links.oneNoteWebUrl.href,
        null,
        null,
        new Date(classnotebook.lastModifiedTime),
        null,
        null,
        FileType.OneNote,
        null,
        FileItem.NameToProvenance[classnotebook.name], //TODO: provenance not given?
        getPathFromUrl(classnotebook.links.oneNoteWebUrl.href),
        isRtl,
        authType,
        fromCache,
        FileItem.fileTypeNameFromType(FileType.OneNote),
        null,
        null,
        true,
        false,
        {
            fullName: classnotebook.lastModifiedBy,
            email: null
        },
        null,
        null,
        null,
        null,
        null,
        false,
        false,
        new Date(classnotebook.createdTime)
    );
}

export function toFileItemFromShared(
    sharedData: SharedServiceDataItem,
    elementId: string,
    impressionId: string,
    isRtl: boolean,
    authType: AuthType,
    fromCache: boolean
): MruFileItem {
    let sharepointIds: GraphSharepointIds;
    const rawSharepointIds = sharedData.SharepointIds;
    if (rawSharepointIds && rawSharepointIds.ListId && rawSharepointIds.ListItemUniqueId && rawSharepointIds.SiteId && rawSharepointIds.SiteUrl && rawSharepointIds.WebId) {
        sharepointIds = {
            listId: rawSharepointIds.ListId,
            listItemId: null,
            listItemUniqueId: rawSharepointIds.ListItemUniqueId,
            siteId: rawSharepointIds.SiteId,
            siteUrl: rawSharepointIds.SiteUrl,
            webId: rawSharepointIds.WebId
        };
    }
    const sharedDate = new Date(sharedData.SharedDate);
    const isClassicAttachment = sharedData.EmailReference && sharedData.EmailReference.AttachmentType === AttachmentType.Classic;
    return new MruFileItem(
        MruFileFormat.Shared,
        elementId,
        impressionId,
        sharedData.ItemId,
        sharedData.DriveId,
        sharedData.FileName,
        FileItem.fileNameWithoutExtension(sharedData.FileName),
        sharedData.DocumentUrl,
        sharedData.WebUrl,
        null,
        new Date(sharedData.LastModifiedDate),
        null,
        sharedDate,
        FileItem.fileTypeNameToFileType(sharedData.Application),
        sharedData.SharingType,
        FileItem.NameToProvenance[sharedData.FileName], //TODO: provenance not given?
        isClassicAttachment ? [] : getPathFromUrl(sharedData.DocumentUrl),
        isRtl,
        authType,
        fromCache,
        sharedData.Application,
        sharedData.ResourceId,
        sharedData.StorageHost,
        true,
        false,
        null,
        null,
        null,
        null,
        null,
        null,
        !!sharedData.EmailReference,
        false,
        new Date(sharedData.CreatedDate),
        sharepointIds,
        null,
        undefined,
        sharedData.EmailReference,
        sharedData.SharedByUserName,
        sharedData.SharedByUserEmail,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        sharedData.ItemId,
        sharedData.CreatedByUserName,
        null,
        sharedData.OwnerType,
        sharedData.OwnerId
    );
}

export function toMruDataItem(fileItem: MruFileItem): MruDataItem {
    return {
        DocId: fileItem.docId,
        Extension: fileItem.extension,
        FileName: fileItem.fileName,
        Title: fileItem.title,
        Url: fileItem.documentUrl,
        WebUrl: fileItem.webUrl,
        LastAccessedTime: fileItem.lastViewedTime,
        FileType: fileItem.fileTypeName,
        Provenance: fileItem.provenanceName,
        PathArray: fileItem.pathArray,
        IsPinned: fileItem.isPinned,
        Application: fileItem.application,
        ResourceId: fileItem.resourceId,
        StorageHost: fileItem.storageHost,
        Category: fileItem.mruV2Category,
        MruV2Type: fileItem.mruV2Type,
        MruId: fileItem.mruV2Id,
        Activity: fileItem.activity,
        DriveId: fileItem.driveId,
        EmailReference: fileItem.emailReference,
        IsEmailAttachment: fileItem.isEmailAttachment,
        IsLocal: fileItem.isLocal
    };

}
